import { inject } from '@angular/core';
import {
  ResolveFn,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { from, of, catchError, map, forkJoin } from 'rxjs';
import { ClientPersonalDetails } from 'src/app/shared/models/client/personal.model';
import { ClientService } from 'src/app/shared/services/client/client.service';
import { FactFindService } from 'src/app/shared/services/fact-find/fact-find.service';
import { slugToSectionType } from 'src/app/shared/utils/fact-find-routing/fact-find-routing.util';
import { ApplicantContextStore } from 'src/app/shared/stores/applicant-context/applicant-context.store';
import { ClientStore } from 'src/app/shared/stores/client/client.store';
import { ApplicationsStore } from 'src/app/shared/stores/applications/applications.store';
import { FactFindStore } from 'src/app/shared/stores/fact-find/fact-find.store';
import { FactFindModeStore } from 'src/app/shared/stores/fact-find/fact-find-mode.store';
import { TemplateStore } from 'src/app/shared/stores/template/template.store';
import {
  FINANCE_TYPE,
  FINANCIAL_PRODUCT_STATUS,
  type ClientApplication,
} from 'src/app/shared/models/applications.model';
import {
  mapApiToQuestions,
  mergeTemplateWithSlimEntry,
} from 'src/app/shared/utils/fact-find-adapter/fact-find-adapter.util';
import { formatDateISO } from 'src/app/shared/utils/common/common.util';
import { enrichJointQuestions } from 'src/app/shared/utils/fact-find-joint-question/fact-find-joint-question.util';
import { FactFindQuestionDto, SectionType, SlimEntry } from 'src/app/shared/models/fact-find-api.model';
import { isCollectionSection } from 'src/app/shared/models/fact-find-collection.config';
import { isPersisted, derivePersistenceState } from 'src/app/shared/models/fact-find-persistence.model';

/**
 * Extracts entryId from fact-find URL for editing scenarios.
 *
 * URL patterns with entryId:
 * - /fact-find/:taskName/:entryId/view-all-question
 * - /fact-find/:taskName/:entryId/:questionId
 *
 * URL patterns WITHOUT entryId (returns null):
 * - /fact-find/:taskName
 * - /fact-find/:taskName/view-all-question
 * - /fact-find/:taskName/:questionId
 *
 * The key distinction: entryId is always followed by a path segment.
 * A number at the end of the URL without a following segment is a questionId.
 */
function parseEntryIdFromUrl(url: string): number | null {
  const match = url.match(/\/fact-find\/[^/]+\/(-?\d+)\//);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id !== 0 ? id : null;
}

function isViewAllUrl(url: string): boolean {
  return url.includes('/view-all-question');
}

function isEligibleBankingApplication(application: ClientApplication | null): boolean {
  return (
    application?.FinanceType === FINANCE_TYPE.Mortgages &&
    application.FinancialProductStatus !== FINANCIAL_PRODUCT_STATUS.Completed
  );
}

function hydrateHealthIdentityFields(
  questions: FactFindQuestionDto[],
  currentClientPersonalDetails: ClientPersonalDetails | null
): FactFindQuestionDto[] {
  if (!currentClientPersonalDetails) {
    return questions;
  }

  const gender = normalizeText(currentClientPersonalDetails.Gender);
  const rawDob = normalizeText(currentClientPersonalDetails.DateOfBirth);
  const dateOfBirth = formatDateISO(rawDob) ?? rawDob;

  if (!gender && !dateOfBirth) {
    return questions;
  }

  return questions.map((question) => {
    if (question.objectName !== 'Client') {
      return question;
    }

    if (question.propertyName === 'Gender' && isBlankSavedValue(question.savedValue) && gender) {
      return { ...question, savedValue: gender };
    }

    if (
      question.propertyName === 'DateOfBirth' &&
      isBlankSavedValue(question.savedValue) &&
      dateOfBirth
    ) {
      return { ...question, savedValue: dateOfBirth };
    }

    return question;
  });
}

function isBlankSavedValue(value: string | boolean | null): boolean {
  if (value == null) return true;
  if (typeof value === 'boolean') return false;
  return value.trim() === '';
}

function normalizeText(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export const factFindSectionResolver: ResolveFn<boolean | UrlTree> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const svc = inject(FactFindService);
  const clientService = inject(ClientService);
  const clientStore = inject(ClientStore);
  const applicantContextStore = inject(ApplicantContextStore);
  const applicationsStore = inject(ApplicationsStore);
  const factFindStore = inject(FactFindStore);
  const modeStore = inject(FactFindModeStore);
  const templateStore = inject(TemplateStore);
  const _router = inject(Router);

  const taskName = route.paramMap.get('taskName') || '';
  const sectionType = slugToSectionType(taskName);

  if (sectionType === null) {
    return of(false);
  }

  const client = clientStore.client();
  const authenticatedClientId = client?.clientId ?? 0;
  if (authenticatedClientId > 0) {
    applicantContextStore.sync(authenticatedClientId, applicationsStore.factFindApplicants());
  }

  const clientId = applicantContextStore.activeClientId() ?? authenticatedClientId;
  const cachedPersonalDetails =
    clientStore.personalDetails?.() ?? client?.personalDetails ?? null;
  const isBanking = sectionType === SectionType.Banking;
  const isNewMortgage = sectionType === SectionType.NewMortgage;
  const isCaseIdSection = isBanking || isNewMortgage;
  const selectedApplication = isCaseIdSection
    ? applicationsStore.mortgageApplication()
    : applicationsStore.factFindContextApplication();

  const caseId = selectedApplication?.ApplicationId ?? 0;

  const isCollection = isCollectionSection(sectionType);
  const loadedType = factFindStore.sectionType();
  const loadedCaseId =
    isCaseIdSection && loadedType === sectionType ? factFindStore.currentEntryId() : null;
  const shouldReloadCaseSection =
    isCaseIdSection && loadedType === sectionType && loadedCaseId !== caseId;
  const hasEligibleBankingMortgage = isEligibleBankingApplication(selectedApplication);

  // Reset on section switch, and always hard-reset caseId-linked sections (Banking, NewMortgage).
  if (
    (loadedType !== null && loadedType !== sectionType) ||
    (shouldReloadCaseSection && loadedType === sectionType)
  ) {
    factFindStore.reset();
    modeStore.reset();
  }

  const alreadyLoaded = loadedType === sectionType && !shouldReloadCaseSection && factFindStore.allIds().length > 0;

  const editingEntryId = parseEntryIdFromUrl(state.url);
  const isEditingSpecificEntry = editingEntryId !== null;
  const isNavigatingToViewAll = isViewAllUrl(state.url);

  if (alreadyLoaded && !isCollection) {
    const currentId = factFindStore.currentEntryId();
    const hasSaved = factFindStore.hasSavedSets();
    if (isCaseIdSection && isNavigatingToViewAll && typeof currentId === 'number' && currentId > 0 && hasSaved) {
      modeStore.toSingleViewing(sectionType, currentId);
      return of(true);
    }
    if (!isEditingSpecificEntry && (currentId === null || currentId === 0)) {
      const savedSets = factFindStore.savedSets();
      if (savedSets.length > 0) {
        const firstSet = savedSets[0];
        if (firstSet && isPersisted(derivePersistenceState(firstSet))) {
          factFindStore.setCurrentEntryId(firstSet.id);
          modeStore.toSingleEditing(sectionType, firstSet.id);
        }
      }
    } else if (isEditingSpecificEntry && editingEntryId) {
      modeStore.toSingleEditing(sectionType, editingEntryId);
    }
    return of(true);
  }

  modeStore.loadSection(sectionType, isCollection);

  // Building Insurance template includes dynamic property options that may change
  // during the session (e.g. after adding a new Existing Property), so always
  // clear the cached template to force a fresh fetch with up-to-date options.
  if (sectionType === SectionType.BuildingInsurance) {
    templateStore.clearTemplate(sectionType);
  }

  // Guard: case-scoped sections require a valid caseId. If factFindApplication()
  // returned null (stale store, no mortgage), initialise empty state and redirect
  // to view-all instead of sending caseId=0 to FFQH.
  if (isCaseIdSection && caseId <= 0) {
    console.warn(
      `[fact-find-section.resolver] No valid application for ${sectionType}. ` +
        `factFindApplication() returned null. Showing empty review state.`
    );
    factFindStore.init(sectionType, [], {}, undefined, [], []);
    modeStore.loadSuccess(0, false);
    modeStore.toSingleViewing(sectionType, 0);

    if (isNavigatingToViewAll) {
      return of(true);
    }

    return of(_router.createUrlTree(['/fact-find', taskName, 'view-all-question']));
  }

  if (isBanking && !hasEligibleBankingMortgage) {
    factFindStore.init(sectionType, [], {}, undefined, [], []);
    modeStore.loadSuccess(0, false);
    modeStore.toSingleViewing(sectionType, 0);

    if (isNavigatingToViewAll) {
      return of(true);
    }

    return of(_router.createUrlTree(['/fact-find', taskName, 'view-all-question']));
  }

  const shouldResolveShareState =
    (
      sectionType === SectionType.Banking ||
      sectionType === SectionType.Dependants ||
      sectionType === SectionType.ExistingProperties ||
      sectionType === SectionType.Expenditure ||
      sectionType === SectionType.NewMortgage
    ) &&
    clientId > 0 &&
    (selectedApplication?.Clients?.length ?? 0) > 1;

  const shouldLoadPersonalDetails =
    (sectionType === SectionType.HealthInformation && clientId > 0) || shouldResolveShareState;

  const currentClientPersonalDetails$ =
    shouldLoadPersonalDetails
      ? clientService.getPersonalDetails(clientId).pipe(
          catchError(() => of<ClientPersonalDetails | null>(cachedPersonalDetails))
        )
      : of<ClientPersonalDetails | null>(cachedPersonalDetails);

  return forkJoin({
    sectionPayload: from(
      svc.loadSectionWithTemplate({
        clientId,
        sectionType,
        caseId: isCaseIdSection ? String(caseId) : undefined,
      })
    ),
    currentClientPersonalDetails: currentClientPersonalDetails$,
  }).pipe(
    map(({ sectionPayload, currentClientPersonalDetails }) => {
      const { template, data } = sectionPayload;
      const savedSets: SlimEntry[] = data?.entries ?? [];
      const hasSavedData = savedSets.length > 0;

      // For editing a specific entry, merge template with that entry's values
      let originals: FactFindQuestionDto[];
      if (isEditingSpecificEntry && hasSavedData && editingEntryId) {
        const targetEntry = savedSets.find((e) => e.id === editingEntryId);
        originals = targetEntry
          ? mergeTemplateWithSlimEntry(template, targetEntry.values)
          : template;
      } else if (hasSavedData && savedSets[0]) {
        // For non-collection sections, consolidate ALL savedSets values into one merged
        // record. Process committed entries (isStaged=false) first, then staged entries
        // (isStaged=true) on top, so staged values always win. Within each group, later
        // entries overwrite earlier ones. Skip empty strings — they represent cleared
        // fields in committed data that may have real values in staging.
        const committed = savedSets.filter((s) => !s.isStaged);
        const staged = savedSets.filter((s) => s.isStaged);
        const consolidatedValues: Record<string, unknown> = {};
        for (const set of [...committed, ...staged]) {
          for (const [key, value] of Object.entries(set.values)) {
            if (value != null && value !== '') consolidatedValues[key] = value;
          }
        }
        originals = mergeTemplateWithSlimEntry(template, consolidatedValues);
      } else {
        originals = template;
      }

      if (sectionType === SectionType.HealthInformation) {
        originals = hydrateHealthIdentityFields(originals, currentClientPersonalDetails);
      }

      originals = enrichJointQuestions({
        sectionType,
        questions: originals,
        currentClientId: clientId,
        selectedApplication,
        currentClientPersonalDetails,
      });

      const questions = mapApiToQuestions(originals);
      let prefilled: Record<number, unknown> = {};
      if (!isCollection) {
        const idByKey: Record<string, number> = {};
        for (const q of questions) {
          const key = (q as unknown as { key?: string }).key;
          if (key) idByKey[String(key).trim()] = q.id;
        }
        const pf: Record<number, unknown> = {};
        for (const dto of originals || []) {
          if (dto?.sectionQuestionNumber != null && dto?.savedValue != null) {
            const key = String(dto.sectionQuestionNumber).trim();
            const id = idByKey[key];
            if (typeof id === 'number') pf[id] = dto.savedValue;
          }
        }
        prefilled = pf;
      }

      factFindStore.init(sectionType, questions, prefilled, undefined, originals, savedSets);

      const hasSaved = factFindStore.hasSavedSets();

      let effectiveEntryId: number | null = null;
      if (isCaseIdSection && caseId > 0) {
        effectiveEntryId = caseId;
        factFindStore.setCurrentEntryId(caseId);
      } else if (!isCollection && hasSaved && savedSets.length > 0) {
        const firstSavedSetId = savedSets[0]?.id;
        if (typeof firstSavedSetId === 'number' && firstSavedSetId > 0) {
          effectiveEntryId =
            isEditingSpecificEntry && editingEntryId ? editingEntryId : firstSavedSetId;
          factFindStore.setCurrentEntryId(effectiveEntryId);
        }
      }

      modeStore.loadSuccess(effectiveEntryId, isCollection);

      // For most flat sections with saved data, redirect to the review screen.
      // Banking is excluded: tester feedback says clicking Banking should open the
      // first question directly, with the review screen accessible via the section icon.
      const shouldRedirectToViewAll =
        hasSaved && !isCollection && !isNavigatingToViewAll && !isEditingSpecificEntry && !isCaseIdSection;
      const shouldUseViewingMode =
        shouldRedirectToViewAll || (isCaseIdSection && isNavigatingToViewAll && hasSaved);

      if (!isCollection && effectiveEntryId && effectiveEntryId > 0) {
        if (shouldUseViewingMode) {
          modeStore.toSingleViewing(sectionType, effectiveEntryId);
        } else {
          modeStore.toSingleEditing(sectionType, effectiveEntryId);
        }
      }

      if (isCollection && isEditingSpecificEntry && editingEntryId) {
        const currentMode = modeStore.mode();
        const alreadySet =
          currentMode.type === 'collection_editing' &&
          (currentMode as { entryId?: number }).entryId === editingEntryId;
        if (!alreadySet) {
          modeStore.toCollectionEditing(sectionType, editingEntryId);
        }
      }

      if (shouldRedirectToViewAll) {
        return _router.createUrlTree(['/fact-find', taskName, 'view-all-question']);
      }
      return true;
    }),
    catchError((err) => {
      modeStore.loadError(err?.message ?? 'Failed to load section');
      return of(false);
    })
  );
};
